"use client";

import { Check, Loader2, MapPin } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useDebounce } from "@/hooks/use-debounce";
import { cn } from "@/lib/utils";
import { useSearchVisaCountryQuery } from "../../../search/api/visa-search-api";
import { IVisaSearchCountry } from "../../../search/types/visa.types";

interface CountrySelectProps {
  value?: IVisaSearchCountry | null;
  onChange: (value: IVisaSearchCountry | null) => void;
  label: string;
  placeholder: string;
  invalid?: boolean;
  disabled?: boolean;
  popoverModal?: boolean;
}

export function VisaCountrySelect({
  value,
  onChange,
  label,
  placeholder,
  invalid = false,
  popoverModal = false,
  disabled = false,
}: CountrySelectProps) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const { data, isLoading, isFetching } = useSearchVisaCountryQuery(
    {
      name: debouncedSearchTerm || undefined,
      limit: 20,
      skip: 0,
    },
    {
      refetchOnReconnect: false,
      refetchOnMountOrArgChange: true,
    },
  );

  const countries = data?.data ?? [];

  return (
    <Popover open={open} onOpenChange={setOpen} modal={popoverModal}>
      <PopoverTrigger asChild disabled={disabled}>
        <Button
          type="button"
          variant="ghost"
          role="combobox"
          aria-label={label}
          aria-expanded={open}
          aria-invalid={invalid}
          disabled={disabled}
          className={cn(
            "h-14 w-full justify-between rounded-md border border-border bg-card px-4 text-left font-normal shadow-none hover:bg-muted/40 sm:h-[56px]",
            !value?.name && "text-muted-foreground",
            invalid && "border-destructive",
          )}
        >
          {value?.name ? (
            <span className="grid min-w-0 flex-1 grid-cols-[auto_1px_minmax(0,1fr)] items-center gap-3">
              <span className="text-base font-bold tracking-tight text-foreground sm:text-[1.05rem]">
                {(value.iso ?? value.iso3 ?? value.name.slice(0, 3)).toUpperCase()}
              </span>
              <span className="h-9 w-px bg-border" />
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold text-foreground/85">
                  {value.name}
                </span>
                <span className="block truncate text-xs text-muted-foreground">
                  {[value.iso3, value.phone_code ? `+${value.phone_code}` : undefined]
                    .filter(Boolean)
                    .join(", ")}
                </span>
              </span>
            </span>
          ) : (
            <span className="min-w-0 text-left">
              <span className="block text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/80">
                {label}
              </span>
              <span className="block truncate text-sm text-muted-foreground">
                {placeholder}
              </span>
            </span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="p-0 h-52 md:h-full" align="start">
        <Command shouldFilter={false}>
          <div className="relative">
            <CommandInput
              value={searchTerm}
              onValueChange={setSearchTerm}
              placeholder="Search countries..."
            />
            {(isLoading || isFetching) && (
              <Loader2 className="text-muted-foreground absolute right-2 top-2 h-4 w-4 animate-spin" />
            )}
          </div>

          <CommandList className="no-scrollbar overscroll-contain touch-pan-y">
            <CommandEmpty>
              {isLoading || isFetching ? "Loading..." : "No results found"}
            </CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="all-country"
                onSelect={() => {
                  onChange(null);
                  setOpen(false);
                }}
                className={cn(
                  "w-full cursor-pointer truncate border-b px-2 py-2",
                  !value?.name && "bg-primary text-primary-foreground",
                )}
              >
                <div className="flex w-full items-center justify-between gap-2">
                  <span className="font-medium">All countries</span>
                  <Check
                    className={cn(
                      "h-4 w-4",
                      !value?.name ? "opacity-100" : "opacity-0",
                    )}
                  />
                </div>
              </CommandItem>

              {countries.map((country, index) => {
                const isSelected =
                  country.id !== undefined && value?.id !== undefined
                    ? String(country.id) === String(value.id)
                    : country.iso === value?.iso;

                return (
                  <CommandItem
                    key={String(country.id ?? country.iso ?? index)}
                    value={String(country.id ?? country.iso ?? index)}
                    onSelect={() => {
                      onChange({
                        id: country.id,
                        iso: country.iso ?? undefined,
                        iso3: country.iso3,
                        name: country.name,
                        phone_code: country.phone_code,
                      });
                      setOpen(false);
                    }}
                    className={cn(
                      "w-full cursor-pointer truncate border-b px-2 py-2",
                      isSelected && "bg-primary text-primary-foreground",
                    )}
                  >
                    <div className="flex w-full items-center gap-2">
                      <div className="flex flex-col min-w-0 flex-1 tracking-tight">
                        <h3
                          className={cn(
                            "font-medium",
                            isSelected
                              ? "text-primary-foreground"
                              : "text-primary",
                          )}
                        >
                          {country.name}
                        </h3>
                        <p className="text-xs flex items-center gap-1 truncate">
                          <MapPin
                            className={cn(
                              "size-3 text-primary",
                              isSelected && "text-primary-foreground",
                            )}
                          />
                          {country.name}
                          {country.iso ? `, ${country.iso}` : ""}
                        </p>
                      </div>

                      <div className="flex items-center gap-2">
                        <span
                          className={cn(
                            "text-xs",
                            isSelected
                              ? "text-primary-foreground"
                              : "text-muted-foreground",
                          )}
                        >
                          {country.phone_code
                            ? `+${country.phone_code}`
                            : country.iso3}
                        </span>
                        <Check
                          className={cn(
                            "h-4 w-4",
                            isSelected ? "opacity-100" : "opacity-0",
                          )}
                        />
                      </div>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
